/**
 * `<Button>` — primary UI action primitive.
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §5.
 * Phase 1 (post-walkthrough): 4 canonical variants × 3 sizes.
 *
 * Variants (canonical):
 *   primary     — brand-grad CTA; single per page surface.
 *   secondary   — border + surface-card; important non-primary actions.
 *   tertiary    — transparent + text-link; low-emphasis inline actions.
 *   destructive — status-rejected tint, hover saturates; destructive ops.
 *
 * @deprecated aliases (hold until Phase 2 sweep):
 *   ghost  → tertiary
 *   danger → destructive
 *
 * Sizes: sm (28px) | md (36px, DEFAULT) | lg (44px hero CTAs).
 */
import { type ButtonHTMLAttributes } from "react";
import { Spinner } from "@/components/common/Spinner";

/** Canonical 4-variant + 2 deprecated-alias set. */
type Variant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "destructive"
  /** @deprecated use "tertiary" */
  | "ghost"
  /** @deprecated use "destructive" */
  | "danger";

type Size = "sm" | "md" | "lg";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
};

const variantClasses: Record<Variant, string> = {
  // Primary = brand-grad CTA. The gradient IS the brand; stays consistent
  // across light/dark surfaces. bg-brand-grad is the CSS-var alias from
  // tailwind.config.ts → tokens.css --brand-grad.
  primary:
    "bg-brand-grad text-text-on-brand shadow-sm hover:bg-brand-grad-hover hover:shadow-md focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-2 disabled:opacity-50",

  // Secondary = border + surface-card; hover lifts to surface-card-hover.
  secondary:
    "bg-surface-card border border-border-strong text-text-primary hover:bg-surface-card-hover focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-2 disabled:opacity-50",

  // Tertiary = transparent + text-link; hover adds subtle bg.
  tertiary:
    "bg-transparent text-text-link hover:bg-surface-subtle focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-2 disabled:opacity-50",

  // Destructive = status-rejected tint; hover saturates to full fg.
  destructive:
    "bg-status-rejected-bg border border-status-rejected-ring text-status-rejected-fg hover:bg-status-rejected-fg hover:text-text-on-brand focus-visible:ring-2 focus-visible:ring-[var(--status-rejected-fg)] focus-visible:ring-offset-2 disabled:opacity-50",

  // Deprecated aliases — hold until Phase 2 call-site sweep.
  /** @deprecated use "tertiary" */
  ghost:
    "bg-transparent text-text-link hover:bg-surface-subtle focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)] focus-visible:ring-offset-2 disabled:opacity-50",

  /** @deprecated use "destructive" */
  danger:
    "bg-status-rejected-bg border border-status-rejected-ring text-status-rejected-fg hover:bg-status-rejected-fg hover:text-text-on-brand focus-visible:ring-2 focus-visible:ring-[var(--status-rejected-fg)] focus-visible:ring-offset-2 disabled:opacity-50",
};

/** Height + padding + text size per size tier. */
const sizeClasses: Record<Size, string> = {
  // sm  = h-7  (28px) px-2.5 text-xs
  sm: "h-7 px-2.5 text-xs font-medium",
  // md  = h-9  (36px) px-3.5 text-sm  ← DEFAULT
  md: "h-9 px-3.5 text-sm font-medium",
  // lg  = h-11 (44px) px-5   text-base  — hero CTAs only
  lg: "h-11 px-5 text-base font-semibold",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  children,
  disabled,
  className = "",
  ...props
}: Props): JSX.Element {
  return (
    <button
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md transition-all focus-visible:outline-none ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
}
